import { type Commands } from '../../../core/context/commands';
import { UUID } from '../../../core/context/uuid/entity';
import { type MatrixValue } from '../../../core/index';
import { type AssetData } from './dto';
import { Asset } from './entity';
import { Assets } from './repository';

export class AssetsCommands implements Commands<Assets, Asset, AssetData> {
    addAllWithMatrix(
        repo: Assets,
        data: AssetData[],
        matrix: MatrixValue[],
    ): Assets {
        repo.clear();
        data.forEach((assetData) => {
            repo.add(
                new Asset({
                    id: assetData.id,
                    name: assetData.name,
                    description: assetData.description ?? undefined,
                    assetType: assetData.assetType?.name,
                    alias: assetData.alias ?? '',
                    inScope: false,
                    zoneId: assetData.projectZone.id,
                    itemIds: (assetData.assetItems ?? []).map(
                        (item) => item.id,
                    ),
                    criticalityId: matrix.find(
                        (m) =>
                            m.name.toLowerCase() ===
                            assetData.systemRating.toLowerCase(),
                    )?.id,
                    status: assetData.status,
                    attributes: assetData.attributes,
                }),
            );
        });
        return repo;
    }

    addAll(repo: Assets, data: AssetData[]): Assets {
        repo.clear();
        data.forEach((assetData) => {
            this.add(repo, assetData);
        });
        return repo;
    }

    add(repo: Assets, data: AssetData): Assets {
        const asset = new Asset({
            id: data.id,
            name: data.name,
            description: data.description ?? undefined,
            assetType: data.assetType?.name,
            alias: data.alias ?? '',
            inScope: false,
            zoneId: data.projectZone.id,
            itemIds: (data.assetItems ?? []).map((item) => item.id),
            status: data.status,
            attributes: data.attributes,
        });
        repo.add(asset);
        return repo;
    }

    update(repo: Assets, entity: Asset): Asset {
        repo.update(entity);
        return entity;
    }

    remove(repo: Assets, id: string): void {
        repo.remove(id);
    }

    create(repo: Assets, data: Omit<AssetData, 'id'>): Asset {
        const uuid = new UUID();
        const asset = new Asset({
            id: uuid.toString(),
            name: data.name,
            description: data.description ?? '',
            assetType: data.assetType?.name,
            alias: data.alias ?? '',
            inScope: false,
            zoneId: data.projectZone.id,
            itemIds: (data.assetItems ?? []).map((item) => item.id),
            status: data.status,
            attributes: data.attributes,
        });
        repo.add(asset);
        return asset;
    }

    fromJSON(entity: Asset[], changeCallback: () => void): Assets {
        const newAssets = new Assets(changeCallback);
        entity.forEach((asset) => {
            newAssets.add(
                new Asset({
                    ...asset,
                }),
            );
        });
        return newAssets;
    }
}
